// Keeps a page usable behind Google Identity-Aware Proxy (dev, preprod and the per-PR
// previews; never prod).
//
// IAP never sends an API call to Google sign-in. A same-origin call with no IAP session and the
// `X-Requested-With: XMLHttpRequest` header gets 401 with `x-goog-iap-generated-response: true`.
// Without that header IAP answers 302 to accounts.google.com, which fetch follows cross-origin
// and reports as an opaque network error. Two pages meet the refusal in normal use: a page whose
// shell came from a public bucket (seller, BI, every preview) and so never passed IAP, and any
// tab whose IAP session expired while it stayed open. The guard does two things:
//
// 1. It adds `X-Requested-With: XMLHttpRequest` to every same-origin fetch, so a refusal is a
//    readable 401 instead of a failed redirect.
// 2. On that 401 it sends the WHOLE PAGE through IAP once: a top-level navigation to
//    `/api/edge/session?to=<this page>` (backend `web/routes/edge.py`). IAP signs the browser in
//    on the way, silently when Google already has a session, and the route sends it back here.
//
// A call made with `credentials: "omit"` gets the header but never the navigation. It cannot
// carry IAP's cookie, so IAP refuses it whatever the state of the session, and a navigation
// cannot change that.
//
// Install it once at boot, and only in a build that sits behind IAP (`VITE_IAP_GATE=1`). A loop
// guard allows one navigation per IAP_REAUTH_MIN_INTERVAL_MS, so a user IAP refuses outright
// (no access) stops on IAP's own page instead of bouncing. This module has no side effects on
// import, so an entry point can import it before anything else.

export const IAP_SESSION_PATH = "/api/edge/session";
export const IAP_RESPONSE_HEADER = "x-goog-iap-generated-response";
export const IAP_REAUTH_MIN_INTERVAL_MS = 30_000;
const REAUTH_AT_KEY = "recruiter.iap.reauth_at";
const XHR_HEADER = "X-Requested-With";

/** The slice of `window` the guard uses, so a test can hand it a fake one. */
export interface IapGuardTarget {
  fetch: typeof fetch;
  location: Pick<Location, "origin" | "href" | "pathname" | "search" | "hash" | "assign">;
  sessionStorage?: Pick<Storage, "getItem" | "setItem">;
}

const guarded = new WeakSet<object>();

/** True when IAP itself refused the call (not the app saying 401). */
export function isIapRefusal(response: Response): boolean {
  return response.status === 401 && response.headers.get(IAP_RESPONSE_HEADER) === "true";
}

/** The navigation that takes this page through IAP and back to where it is now. */
export function iapSessionUrl(location: Pick<Location, "pathname" | "search" | "hash">): string {
  const here = `${location.pathname}${location.search}${location.hash}`;
  return `${IAP_SESSION_PATH}?to=${encodeURIComponent(here)}`;
}

function requestUrl(input: RequestInfo | URL): string {
  if (input instanceof Request) return input.url;
  if (input instanceof URL) return input.href;
  return String(input);
}

/** The cookie mode of a call: `init` overrides a `Request`'s own, and fetch defaults to "same-origin". */
function credentialsOf(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
): RequestCredentials {
  return init?.credentials ?? (input instanceof Request ? input.credentials : "same-origin");
}

function isSameOrigin(url: string, location: IapGuardTarget["location"]): boolean {
  try {
    return new URL(url, location.href).origin === location.origin;
  } catch {
    return false;
  }
}

/** The same call, carrying `X-Requested-With: XMLHttpRequest` unless it already has the header. */
function markAsXhr(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
): [RequestInfo | URL, RequestInit | undefined] {
  if (input instanceof Request && init === undefined) {
    if (input.headers.has(XHR_HEADER)) return [input, init];
    const headers = new Headers(input.headers);
    headers.set(XHR_HEADER, "XMLHttpRequest");
    return [new Request(input, { headers }), undefined];
  }
  const headers = new Headers(
    init?.headers ?? (input instanceof Request ? input.headers : undefined),
  );
  if (!headers.has(XHR_HEADER)) headers.set(XHR_HEADER, "XMLHttpRequest");
  return [input, { ...init, headers }];
}

/** Wrap `target.fetch` (default: `window`). Idempotent per target. */
export function installIapSessionGuard(target: IapGuardTarget = window): void {
  if (guarded.has(target)) return;
  guarded.add(target);
  const originalFetch = target.fetch.bind(target);
  let reauthAt = 0;

  const reauthenticate = (): void => {
    const now = Date.now();
    let last = reauthAt;
    try {
      last = Math.max(last, Number(target.sessionStorage?.getItem(REAUTH_AT_KEY) ?? 0) || 0);
    } catch {
      // Storage blocked: the in-memory mark still stops a loop within this page load.
    }
    if (now - last < IAP_REAUTH_MIN_INTERVAL_MS) return;
    reauthAt = now;
    try {
      target.sessionStorage?.setItem(REAUTH_AT_KEY, String(now));
    } catch {
      // Same as above.
    }
    target.location.assign(iapSessionUrl(target.location));
  };

  target.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    if (!isSameOrigin(requestUrl(input), target.location)) return originalFetch(input, init);
    const [markedInput, markedInit] = markAsXhr(input, init);
    const response = await originalFetch(markedInput, markedInit);
    if (isIapRefusal(response) && credentialsOf(input, init) !== "omit") reauthenticate();
    return response;
  };
}
